import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DeathData, formatDeathRelationship, formatDeathState, getDeathStateColor } from '@/domain/deathData/deathData'
import { calculateAgeStr } from '@/utils/dates'
import { cn } from '@/utils/tailwind-util'
import { format } from 'date-fns'
import { Calendar, ChevronDown, ChevronUp, ClipboardList, Edit, FileText, IdCardIcon, MapPin, Stethoscope, Trash, User } from 'lucide-react'
import { useState } from 'react'

interface OccupantItemProps {
  occ: DeathData
  onEdit: (occ: DeathData) => void
  onDelete: (occ: DeathData) => void
  disabled?: boolean
}

export const OccupantItem = ({ occ, onEdit, onDelete, disabled }: OccupantItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const detailItem = (icon: any, label: string, value: string | null | undefined) => {
    if (!value) return null
    const Icon = icon
    return (
      <div className="flex items-start gap-2 p-2 rounded-lg bg-gray-50/50 border border-gray-100">
        <div className="mt-0.5 text-primary/60">
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div>
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-tight leading-none mb-1">{label}</p>
          <p className="text-xs font-medium text-foreground">{value}</p>
        </div>
      </div>
    )
  }

  const dateItem = (label: string, date: Date | null | undefined) => {
    if (!date) return null
    return (
      <div className="flex flex-col p-2 rounded-lg bg-blue-50/30 border border-blue-100/50">
        <p className="text-[10px] font-bold text-blue-700/60 uppercase tracking-tight leading-none mb-1">{label}</p>
        <p className="text-xs font-mono font-medium text-blue-900">{format(new Date(date), "dd/MM/yyyy")}</p>
      </div>
    )
  }
  
  return (
    <div className={cn(
      "bg-white border rounded-xl shadow-sm transition-all duration-200 overflow-hidden group mb-3 last:mb-0",
      isExpanded ? "ring-2 ring-primary/10 shadow-md border-primary/20" : "hover:border-primary/20"
    )}>
      {/* Main Row */}
      <div className="p-3 grid grid-cols-[auto_1fr_auto_auto] gap-4 items-center">
        <div className="bg-primary/10 p-2.5 rounded-full shrink-0">
          <User className="h-5 w-5 text-primary" />
        </div>

        <div className="min-w-0 flex flex-col">
          <p className="font-bold text-sm text-slate-900 capitalize truncate leading-none mb-1.5">{occ.name?.toLowerCase()}</p>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1">
              <IdCardIcon className="h-3.5 w-3.5 flex-shrink-0" />
              <p className="text-xs font-medium text-foreground">{!occ.docType || !occ.docNumber ? 'S/N Doc' : `${occ.docType} ${occ.docNumber}`}</p>
            </div>
            <span className="text-xs text-muted-foreground border-l border-slate-300 pl-2">
              {occ.birthDate ? format(new Date(occ.birthDate), "dd/MM/yyyy") : 'S/F Nac.'}
              {occ.birthDate || occ.deathDate ? ' - ' : ''}
              {occ.deathDate ? format(new Date(occ.deathDate), "dd/MM/yyyy") : 'S/F Def.'}
            </span>
            {occ.birthDate && occ.deathDate && (
              <span className="text-xs font-bold text-slate-700">
                ({calculateAgeStr(new Date(occ.birthDate), occ.deathDate ? new Date(occ.deathDate) : undefined)})
              </span>
            )}
            {occ.relationshipWithDeceased && (
              <span className="text-xs text-muted-foreground border-l border-slate-300 pl-2 italic">
                {formatDeathRelationship(occ.relationshipWithDeceased)}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 min-w-[100px]">
          {occ.state ? (
            <Badge className={cn("px-2 py-0.5 text-[10px] font-bold uppercase", getDeathStateColor(occ.state))}>
              {formatDeathState(occ.state)}
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px]">Sin estado</Badge>
          )}
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className={cn("h-8 w-8 rounded-full transition-colors", isExpanded ? "bg-primary/10 text-primary" : "hover:bg-gray-100 text-muted-foreground")}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Expanded Details Section */}
      {isExpanded && (
        <div className="border-t bg-gray-50/30 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-4 space-y-4">
            {/* Row 1: Dates */}
            <div className="space-y-3">
              <h5 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-2 px-1 flex items-center gap-1.5">
                <Calendar className="h-4 w-4 flex-shrink-0" />Datos del fallecido
              </h5>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {dateItem("Nacimiento", occ.birthDate)}
                {dateItem("Defunción", occ.deathDate)}
                {dateItem("Inhumación", occ.intermentDate)}
                {dateItem("Reducción", occ.reductionDate)}
                {dateItem("Cremación", occ.cremationDate)}
                {dateItem("Exhumación", occ.disintermentDate)}
              </div>
            </div>

            <h5 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-2 px-1 flex items-center gap-1.5 border-t pt-4">
              <ClipboardList className="h-4 w-4 flex-shrink-0" />Datos del Registro Civil
            </h5>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {detailItem(ClipboardList, "Acta", occ.entryNumber)}
              {detailItem(Calendar, "Año", occ.year?.toString())}
              {detailItem(ClipboardList, "Tomo", occ.volume)}
              {detailItem(ClipboardList, "Folio", occ.leaf)}
              {detailItem(MapPin, "Oficina", occ.office)}
              {detailItem(MapPin, "Funeraria", occ.mortuary)}
              {detailItem(Stethoscope, "Médico", occ.medicOfDeceased)}
              {detailItem(FileText, "Autorización de sepultura", occ.deathCertificateAuthorization)}
            </div>

            {occ.transferredTo && (
              <div className="mt-4 p-3 bg-white border border-gray-100 rounded-lg shadow-inner">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-tight mb-1">Trasladado a</p>
                <p className="text-xs text-slate-600 leading-relaxed">{occ.transferredTo}</p>
              </div>
            )}

            {occ.notes && (
              <div className="mt-4 p-3 bg-white border border-gray-100 rounded-lg shadow-inner">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-tight mb-1">Notas</p>
                <p className="text-xs text-slate-600 leading-relaxed italic">{occ.notes}</p>
              </div>
            )}
          </div>
          {!disabled && (
            <div className="p-4 border-t flex justify-between gap-2">
              <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => onDelete(occ)}>
                <Trash className="h-4 w-4 mr-2" />
                Eliminar
              </Button>
              <Button variant="outline" size="sm" className="text-blue-600 hover:bg-blue-50 hover:text-blue-700" onClick={() => onEdit(occ)}>
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
